import React, { useState } from 'react';

/**
 * Fast Graph Engine settings grid.
 *
 * Renders the user-tunable knobs for the precision pipeline
 * (`poc_snn_graph_nolm.v3.precision_pipeline.PrecisionGraphKnobs`).
 * Bound to the same `globalParams` object the LLM grid uses; round-trips
 * via `processing_params` -> documents row -> worker -> GraphBuilder route.
 *
 * LLM Atomization Budget is the only essential visible by default.
 * The remaining seven toggles live behind a collapsible "Advanced"
 * section so the panel stays clean for typical use.
 */
function FastGraphSettingsGrid({ globalParams, setGlobalParams }) {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const update = (key, value) => setGlobalParams({ ...globalParams, [key]: value });

  return (
    <div className="settings-grid">
      <div className="settings-section-header">Fast Graph Engine</div>

      <div className="setting-item">
        <label>LLM Atomization Budget</label>
        <input
          type="number"
          min="0"
          max="500"
          step="10"
          value={globalParams.fge_llm_budget ?? 150}
          onChange={(e) => update('fge_llm_budget', parseInt(e.target.value, 10) || 0)}
        />
        <span className="hint">
          Max OpenAI atomization calls per document. <strong>0 = pure rule-based</strong> (no LLM).
          Recommended: 150-250 for legal/policy docs (richer atoms = more synapses = better
          synaptic coverage).
        </span>
      </div>

      {/* Coverage Threshold (%) slider removed: the per-chunk success score in
          _compute_coverage_metrics_precision is currently binary (0/100 based on
          node presence), so the threshold has no behavioral effect on the build.
          PrecisionGraphKnobs.coverage_threshold keeps its dataclass default of 70
          server-side. Reintroduce when graded scoring (e.g. char_coverage_percent)
          is wired into the success comparison. */}

      <div className="settings-section-header" style={{ cursor: 'pointer' }}
           onClick={() => setShowAdvanced(!showAdvanced)}>
        Advanced settings <span className="toggle-icon">{showAdvanced ? '▼' : '▶'}</span>
      </div>

      {showAdvanced && (
        <>
          <div className="setting-item checkbox-item">
            <label>
              <input
                type="checkbox"
                checked={globalParams.fge_extract_images !== false}
                onChange={(e) => update('fge_extract_images', e.target.checked)}
              />
              Extract images
            </label>
            <span className="hint">Build verbatim_image nodes for embedded figures.</span>
          </div>

          {globalParams.fge_extract_images !== false && (
            <div className="setting-item">
              <label>Vision model</label>
              <input
                type="text"
                value={globalParams.fge_vision_model ?? 'gpt-4o'}
                onChange={(e) => update('fge_vision_model', e.target.value)}
                placeholder="gpt-4o"
              />
              <span className="hint">Vision model used to describe figures.</span>
            </div>
          )}

          <div className="setting-item checkbox-item">
            <label>
              <input
                type="checkbox"
                checked={globalParams.fge_enable_xref !== false}
                onChange={(e) => update('fge_enable_xref', e.target.checked)}
              />
              Resolve cross-references
            </label>
            <span className="hint">Link "see Section X" mentions to their targets.</span>
          </div>

          <div className="setting-item checkbox-item">
            <label>
              <input
                type="checkbox"
                checked={globalParams.fge_enable_coref !== false}
                onChange={(e) => update('fge_enable_coref', e.target.checked)}
              />
              Resolve coreference
            </label>
            <span className="hint">Merge pronoun + entity references into one node.</span>
          </div>

          <div className="setting-item checkbox-item">
            <label>
              <input
                type="checkbox"
                checked={globalParams.fge_enable_tables !== false}
                onChange={(e) => update('fge_enable_tables', e.target.checked)}
              />
              Extract tables
            </label>
            <span className="hint">Build verbatim_table + table_row nodes from tables.</span>
          </div>

          <div className="setting-item checkbox-item">
            <label>
              <input
                type="checkbox"
                checked={globalParams.fge_enable_vocab_forge !== false}
                onChange={(e) => update('fge_enable_vocab_forge', e.target.checked)}
              />
              Vocabulary forge
            </label>
            <span className="hint">Mine domain noun phrases for the dictionary.</span>
          </div>

          <div className="setting-item checkbox-item">
            <label>
              <input
                type="checkbox"
                checked={globalParams.fge_enable_typer_v2 !== false}
                onChange={(e) => update('fge_enable_typer_v2', e.target.checked)}
              />
              Type resolution v2
            </label>
            <span className="hint">Embedding-NN node typing (default on).</span>
          </div>

          <div className="setting-item checkbox-item">
            <label>
              <input
                type="checkbox"
                checked={globalParams.fge_enable_domain_ontology !== false}
                onChange={(e) => update('fge_enable_domain_ontology', e.target.checked)}
              />
              Domain ontology classification
            </label>
            <span className="hint">
              LLM-classify each node's real-world domain (address, medical code, monetary amount, etc.).
              Adds ~2-4 min to build time.
            </span>
          </div>
        </>
      )}
    </div>
  );
}

export default FastGraphSettingsGrid;
